import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';
import Spinner from '../../../components/ui/Spinner';

const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: T.white,
  border: `1px solid ${T.border}`,
  borderRadius: 16,
  padding: 14,
  boxShadow: T.shadow,
  ...extra,
});

const sectionTitle = (text: string) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 8 }}>{text}</div>
);

const pill = (text: string, color: string, filled = false): React.CSSProperties => ({
  fontSize: 9, fontWeight: 600,
  color: filled ? '#FFF' : color,
  background: filled ? color : `${color}12`,
  padding: '3px 10px', borderRadius: 6, fontFamily: T.font,
  border: filled ? 'none' : `1px solid ${color}25`,
});

export default function PatientMedicalBasics() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [activeMeds, setActiveMeds] = useState<{ name: string; dose: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;
    // Full profile (blood group, allergies, conditions, surgeries) — the
    // dashboard summary's profile stub only carries name + NHC ID. Active
    // medicines come from the real reminders list; each feed fails
    // independently.
    Promise.all([
      patientApi.getProfile().catch(() => null),
      patientApi.getMedicines().catch(() => []),
    ])
      .then(([p, meds]) => {
        if (!alive) return;
        if (p) { setProfile(p); setLoadError(false); } else { setLoadError(true); }
        setActiveMeds(
          meds
            .filter(m => m.status === 'ACTIVE')
            .map(m => ({ name: m.medicineName, dose: [m.dosage, m.frequency?.replace(/_/g, ' ').toLowerCase()].filter(Boolean).join(' · ') })),
        );
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  const bloodGroup = profile?.bloodGroup || 'N/A';
  const allergies: string[] = profile?.allergies ?? [];
  const conditions: string[] = profile?.chronicConditions ?? [];
  const surgeries: string[] = profile?.surgeries ?? [];

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>

      {loadError && (
        <div style={{ fontSize: 10, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '8px 12px', fontFamily: T.font }}>
          Could not load your medical profile from the server. Please refresh the page.
        </div>
      )}

      <div style={card()}>
        {sectionTitle('Blood Group')}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 20, fontWeight: 800, color: T.danger, fontFamily: T.font }}>{bloodGroup}</span>
          <button onClick={() => navigate('/patient/settings?tab=profile&focus=bloodGroup')} style={{ fontSize: 9, fontWeight: 600, color: T.teal, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.font }}>Edit →</button>
        </div>
      </div>

      <div style={card()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          {sectionTitle('Allergies')}
          <button onClick={() => navigate('/patient/settings?tab=profile&focus=allergies')} style={{ fontSize: 9, fontWeight: 600, color: T.teal, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.font }}>Edit →</button>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {allergies.length > 0
            ? allergies.map((a: string) => <span key={a} style={pill(a, T.danger, true)}>{a}</span>)
            : <span style={{ fontSize: 10, color: T.textSec, fontFamily: T.font }}>No allergies recorded. Tap Edit to add.</span>}
        </div>
      </div>

      <div style={card()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          {sectionTitle('Chronic Conditions')}
          <button onClick={() => navigate('/patient/settings?tab=profile&focus=conditions')} style={{ fontSize: 9, fontWeight: 600, color: T.teal, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.font }}>Edit →</button>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {conditions.length > 0
            ? conditions.map((c: string) => <span key={c} style={pill(c, T.purple, true)}>{c}</span>)
            : <span style={{ fontSize: 10, color: T.textSec, fontFamily: T.font }}>No conditions recorded. Tap Edit to add.</span>}
        </div>
      </div>

      <div style={card()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          {sectionTitle('Current Medicines')}
          <button onClick={() => navigate('/patient/medicines')} style={{ fontSize: 9, fontWeight: 600, color: T.teal, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.font }}>Manage →</button>
        </div>
        {activeMeds.length > 0
          ? activeMeds.map((m, i) => (
            <div key={`${m.name}-${i}`} style={{ padding: '6px 0', borderTop: i > 0 ? `1px solid ${T.border}` : 'none' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.text, fontFamily: T.font }}>{m.name}</div>
              {m.dose && <div style={{ fontSize: 8, color: T.textTer, fontFamily: T.font }}>{m.dose}</div>}
            </div>
          ))
          : <span style={{ fontSize: 10, color: T.textSec, fontFamily: T.font }}>No active medicines. Tap Manage to add reminders.</span>}
      </div>

      <div style={card()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          {sectionTitle('Past Surgeries')}
          <button onClick={() => navigate('/patient/settings?tab=profile&focus=surgeries')} style={{ fontSize: 9, fontWeight: 600, color: T.teal, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.font }}>Edit →</button>
        </div>
        {surgeries.length > 0
          ? surgeries.map((s, i) => (
            <div key={s} style={{ padding: '6px 0', borderTop: i > 0 ? `1px solid ${T.border}` : 'none' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.text, fontFamily: T.font }}>{s}</div>
            </div>
          ))
          : <span style={{ fontSize: 10, color: T.textSec, fontFamily: T.font }}>No surgeries recorded. Tap Edit to add.</span>}
      </div>

      <div style={card()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          {sectionTitle('Family Medical History')}
          <button onClick={() => navigate('/patient/settings?tab=profile&focus=familyHistory')} style={{ fontSize: 9, fontWeight: 600, color: T.teal, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.font }}>Edit →</button>
        </div>
        {profile?.familyHistory && profile.familyHistory.length > 0
          ? profile.familyHistory.map((f: string, i: number) => (
            <div key={f} style={{ padding: '6px 0', borderTop: i > 0 ? `1px solid ${T.border}` : 'none' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.text, fontFamily: T.font }}>{f}</div>
            </div>
          ))
          : <span style={{ fontSize: 10, color: T.textSec, fontFamily: T.font }}>No family history recorded. Tap Edit to add.</span>}
      </div>

      <div style={{ ...GOV_COMPLIANCE.text, marginTop: 12 }}>
        Medical data is encrypted and consent-based · NeoHealthCard™
      </div>
    </div>
  );
}
